import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { endpoints } from '../api/endpoints';
import { ErrorBox, formatDate, Loading, useAsync } from '../components/common';

export function ScenariosPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const list = useAsync(() => endpoints.scenarios.list({ search, status }), [search, status]);
  const templates = useAsync(() => endpoints.templates.list().catch(() => []), []);
  const [error, setError] = useState<unknown>(null);

  return (
    <div>
      <div className="page-header">
        <h1>{t('scenarios.title')}</h1>
        <Link className="button" to="/scenarios/new">
          {t('scenarios.create')}
        </Link>
      </div>
      <div className="filters">
        <label>
          {t('common.search')}
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label>
          {t('scenarios.statusField')}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">—</option>
            {(['draft', 'published', 'archived'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`scenarios.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ErrorBox error={list.error || error} onRetry={list.reload} />
      {list.loading && <Loading />}
      {!list.loading && !list.data?.length && <p className="muted">{t('scenarios.empty')}</p>}
      {Boolean(list.data?.length) && (
        <table>
          <thead>
            <tr>
              <th scope="col">{t('scenarios.titleField')}</th>
              <th scope="col">{t('scenarios.version')}</th>
              <th scope="col">{t('scenarios.statusField')}</th>
              <th scope="col">{t('scenarios.tags')}</th>
              <th scope="col">{t('sessions.started')}</th>
            </tr>
          </thead>
          <tbody>
            {list.data?.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={`/scenarios/${s.id}`}>{s.title}</Link>
                </td>
                <td>{s.semantic_version ?? s.version}</td>
                <td>{t(`scenarios.status.${s.status}`, { defaultValue: s.status })}</td>
                <td>{(s.tags ?? []).join(', ')}</td>
                <td>{formatDate(s.updated_at ?? s.created_at, i18n.language)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <section>
        <h2>{t('scenarios.templates')}</h2>
        <ul className="card-list">
          {(templates.data ?? []).map((tpl) => (
            <li key={tpl.id} className="card">
              <h3>{tpl.name}</h3>
              <p className="muted">{tpl.description}</p>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const created = await endpoints.templates.instantiate(tpl.id, tpl.name);
                    navigate(`/scenarios/${created.id}`);
                  } catch (err) {
                    setError(err);
                  }
                }}
              >
                {t('scenarios.fromTemplate')}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
